// =============================================================================
// app/src/lib/loja3d.ts — a loja em três dimensões, para o mapa (/mapa).
//
// O QUE ESTÁ DENTRO da loja (paredes, estantes, prateleiras, ganchos, calotas,
// escadas…) mora em `planta.json`, que o editor do próprio mapa grava. Aqui
// ficam a moldura — andares, zonas, telhados — e a leitura daquele arquivo.
//
// DE ONDE SAÍRAM OS NÚMEROS: do desenho à mão do dono (CamScanner de
// 24/09/2026) conferido contra os vídeos de 25/09/2026 (IMG_8420 térreo, 8422
// fundos, 8423 sótão, 8424 2º andar). Nada foi medido com trena: a largura da
// loja foi estimada em 8 m. O editor existe para o dono acertar o resto.
//
// Eixos, sempre em METROS:
//   x — largura. 0 é a parede ESQUERDA de quem entra pela frente: a das janelas
//       do 2º andar e do portão dos fundos. As duas escadas ficam na direita.
//   y — profundidade. 0 é a fachada, na rua; cresce para os fundos.
//   z — altura, contada do piso do PRÓPRIO andar.
//
// As paredes são desenhadas cortadas a 1,20 m, como numa planta: inteiras,
// esconderiam as estantes que o mapa existe para mostrar.
// =============================================================================
import plantaDoArquivo from './planta.json';
import { ehMarcaDaCalota, type MarcaDaCalota } from './marcasDasCalotas';

export type IdAndar = 1 | 2 | 3;
export type CodigoZona = 'Z1' | 'Z2' | 'Z3' | 'Z4' | 'Z5';
export const CODIGOS_DE_ZONA: readonly CodigoZona[] = ['Z1', 'Z2', 'Z3', 'Z4', 'Z5'];

export interface Andar {
  id: IdAndar;
  nome: string;
  /** Altura do piso na loja montada, em metros a partir da calçada. */
  cota: number;
}

// O dono chama o térreo de "1º andar" (resposta de 24/09/2026) — e é assim que
// as escadas do desenho se referem a ele. O mapa fala a língua da loja.
export const ANDARES: readonly Andar[] = [
  { id: 1, nome: '1º andar', cota: 0 },
  { id: 2, nome: '2º andar', cota: 3.0 },
  { id: 3, nome: '3º andar', cota: 5.6 },
];

export interface Zona {
  codigo: CodigoZona;
  andar: IdAndar;
  nome: string;
  /** Contorno no chão do andar, em metros (x, y). */
  contorno: ReadonlyArray<readonly [number, number]>;
  descricao: string;
  /** As classes de peça que o DONO disse que moram ali. Vazio = ainda não disse. */
  classes: readonly string[];
}

export const ZONAS: readonly Zona[] = [
  {
    codigo: 'Z1', andar: 1, nome: 'Loja (frente)',
    contorno: [[0, 0], [8, 0], [8, 8.1], [0, 8.1]],
    descricao: 'O atendimento: o balcão em L com a parede vermelha atrás, os óleos na parede da '
      + 'direita e as calotas em cinco grupos de ganchos (E, C, P, D, V) e em pilhas no chão — toque num gancho para '
      + 'ver a parede dele de frente. Tem pé-direito duplo — do corredor do 2º andar se enxerga a '
      + 'loja lá embaixo. A porta de vidro da direita é a escada do 3º andar.',
    classes: [],
  },
  {
    codigo: 'Z2', andar: 1, nome: 'Estantes do meio',
    contorno: [[0, 8.1], [8, 8.1], [8, 17.5], [0, 17.5]],
    descricao: 'Quatro corredores de estante de aço, cada um com a marca na placa do teto (1 Fiat, '
      + '2 Ford, 3 Chevrolet, 4 VW), da loja até a cozinha e o banheiro. A escada de madeira, no '
      + 'corredor da VW, sobe para o 2º andar; a porta de ferro, na parede da cozinha, dá na Z3.',
    classes: [],
  },
  {
    codigo: 'Z3', andar: 1, nome: 'Fundos',
    contorno: [[0, 17.5], [8, 17.5], [8, 22], [9.9, 22], [9.9, 26.6], [0, 26.6]],
    descricao: 'O galpão atrás da porta de ferro com cadeado: estantes de madeira, a mesa de '
      + 'trabalho e o portão, com o entulho ao lado.',
    classes: [
      'Lanternas', 'Subconjuntos', 'Retrovisores', 'Capas de retrovisor', 'Bolas de câmbio', 'Lentes',
    ],
  },
  {
    codigo: 'Z4', andar: 2, nome: '2º andar',
    // O 2º andar inteiro, menos o vão sobre a loja e o poço da escada do sótão.
    contorno: [[0, 0], [6.7, 0], [6.7, 2.4], [1.3, 2.4], [1.3, 8.1], [8, 8.1], [8, 17.5], [0, 17.5]],
    descricao: 'Em cima da Z2: estantes, o corredor junto às janelas com vista para a loja e, na '
      + 'frente, o escritório (hoje lotado de caixas). Chega-se pela escada de madeira da Z2.',
    classes: [],
  },
  {
    codigo: 'Z5', andar: 3, nome: 'Sótão (3º andar)',
    contorno: [[0, 0], [8, 0], [8, 14.4], [0, 14.4]],
    descricao: 'Debaixo do telhado: mesas compridas e prateleiras com peças de lataria. Sobe-se '
      + 'pela escada da porta de vidro, na frente da loja.',
    classes: ['Parachoques', 'Rodas'],
  },
];

// --- A planta: o que o arquivo guarda e o editor mexe ------------------------

/**
 * O que se põe na loja. 'escada' vira degraus no desenho.
 *
 * 'vaga' (01/10/2026, pedido do dono: "populasse no mapa os itens para eu
 * preencher clicando em cada peça"): o LUGAR de uma peça — o frasco na
 * prateleira dos Vonixx, o emblema no painel vermelho, o galão no chão. O
 * código dela é mais longo que o da prateleira (PR-FR-V1-01-05 dentro de
 * PR-FR-V1), então a peça com esse endereço mora nela e não na prateleira
 * (lib/zonas.ts ondeMora). Não é gancho: fica fora da vista de frente e da
 * numeração das fileiras (lib/parede.ts).
 */
export type TipoDaPlanta =
  | 'piso' | 'parede' | 'vidro' | 'guarda-corpo' | 'porta'
  | 'estante' | 'madeira' | 'prateleira' | 'painel' | 'gancho' | 'calotas'
  | 'mesa' | 'balcao' | 'movel' | 'caixas' | 'entulho' | 'escada' | 'area' | 'vaga';

export const TIPOS_DA_PLANTA: readonly TipoDaPlanta[] = [
  'piso', 'parede', 'vidro', 'guarda-corpo', 'porta',
  'estante', 'madeira', 'prateleira', 'painel', 'gancho', 'calotas',
  'mesa', 'balcao', 'movel', 'caixas', 'entulho', 'escada', 'area', 'vaga',
];

/** O que o desenho pinta: a escada chega aqui já cortada em degraus. */
export type TipoObjeto = Exclude<TipoDaPlanta, 'escada'> | 'degrau';

/** Cores com nome — a parede vermelha, a prateleira preta. O vermelho da marca fica para a zona escolhida. */
export type Cor = 'preto' | 'vermelho' | 'branco' | 'cinza' | 'madeira' | 'amarelo' | 'azul' | 'prata';
export const CORES: readonly Cor[] = ['preto', 'vermelho', 'branco', 'cinza', 'madeira', 'amarelo', 'azul', 'prata'];

export type Sentido = 'fundo' | 'frente' | 'direita' | 'esquerda';

/** Um objeto como o arquivo o guarda: canto, tamanho e altura, em metros. */
export interface ObjetoDaPlanta {
  id: string;
  andar: IdAndar;
  tipo: TipoDaPlanta;
  nome?: string;
  /**
   * Liga as peças ao lugar: peça cuja localização (stock.local) começa com
   * este código mora aqui — "Z1-CAL07" recebe "Z1-CAL07"; "PR-FR-D" recebe
   * "PR-FR-D-03". Casa por nível, como o resto do mapa (lib/mapa.ts).
   */
  codigo?: string;
  /** Zona forçada. Sem ela, vale a zona que contém o centro do objeto. */
  zona?: CodigoZona | null;
  cor?: Cor;
  /** Canto de menor x e menor y. */
  x: number;
  y: number;
  /** Tamanho ao longo de x e ao longo de y. */
  larg: number;
  fundo: number;
  /** Altura da base acima do piso do andar, e a altura do objeto. Na escada, `alt` é quanto ela sobe. */
  z: number;
  alt: number;
  /** Escada: para onde sobe e em quantos degraus. */
  sobe?: Sentido;
  degraus?: number;
  /** Ponta da gôndola (face da frente) pintada de vermelho, como na loja. */
  pontaVermelha?: boolean;
  /**
   * Gancho de calota (15/10/2026): a MARCA que mora nele. As calotas dessa
   * marca ficam na área formada por estes ganchos — o lugar delas é a área, não
   * um gancho (lib/marcasDasCalotas.ts).
   */
  marca?: MarcaDaCalota;
}

export interface PlantaDaLoja {
  versao: 1;
  objetos: ObjetoDaPlanta[];
}

/**
 * Lê a planta (do arquivo ou de um rascunho) e RECUSA o que não serve, com o
 * nome do objeto no recado: um número errado no arquivo tem que cair aqui, e
 * não virar desenho torto em silêncio.
 */
export function lerPlanta(dado: unknown): PlantaDaLoja {
  const raiz = dado as { versao?: unknown; objetos?: unknown } | null;
  if (!raiz || raiz.versao !== 1 || !Array.isArray(raiz.objetos)) {
    throw new Error('Planta inválida: esperava { versao: 1, objetos: [...] }.');
  }
  const ids = new Set<string>();
  const objetos = raiz.objetos.map((bruto, i): ObjetoDaPlanta => {
    const o = bruto as Record<string, unknown>;
    const onde = `objeto ${i + 1} (${String(o.id ?? 'sem id')})`;
    const num = (campo: string): number => {
      const v = o[campo];
      if (typeof v !== 'number' || !Number.isFinite(v)) throw new Error(`Planta inválida: ${onde} sem "${campo}" numérico.`);
      return v;
    };
    if (typeof o.id !== 'string' || !o.id) throw new Error(`Planta inválida: ${onde} sem id.`);
    if (ids.has(o.id)) throw new Error(`Planta inválida: id repetido "${o.id}".`);
    ids.add(o.id);
    if (o.andar !== 1 && o.andar !== 2 && o.andar !== 3) throw new Error(`Planta inválida: ${onde} com andar fora de 1–3.`);
    if (!TIPOS_DA_PLANTA.includes(o.tipo as TipoDaPlanta)) throw new Error(`Planta inválida: ${onde} com tipo "${String(o.tipo)}".`);
    const larg = num('larg');
    const fundo = num('fundo');
    const alt = num('alt');
    if (larg <= 0 || fundo <= 0 || alt < 0) throw new Error(`Planta inválida: ${onde} com tamanho zero ou negativo.`);
    const obj: ObjetoDaPlanta = {
      id: o.id, andar: o.andar, tipo: o.tipo as TipoDaPlanta,
      x: num('x'), y: num('y'), larg, fundo, z: num('z'), alt,
    };
    if (typeof o.nome === 'string' && o.nome.trim()) obj.nome = o.nome.trim();
    if (typeof o.codigo === 'string' && o.codigo.trim()) obj.codigo = o.codigo.trim();
    if (o.zona === null || CODIGOS_DE_ZONA.includes(o.zona as CodigoZona)) obj.zona = o.zona as CodigoZona | null;
    if (CORES.includes(o.cor as Cor)) obj.cor = o.cor as Cor;
    if (o.pontaVermelha === true) obj.pontaVermelha = true;
    if (obj.tipo === 'calotas' && ehMarcaDaCalota(o.marca)) obj.marca = o.marca;
    if (obj.tipo === 'escada') {
      obj.sobe = (['fundo', 'frente', 'direita', 'esquerda'] as const).includes(o.sobe as Sentido)
        ? o.sobe as Sentido : 'fundo';
      obj.degraus = typeof o.degraus === 'number' && o.degraus >= 2 ? Math.round(o.degraus) : 12;
    }
    return obj;
  });
  return { versao: 1, objetos };
}

// --- O que o desenho usa -----------------------------------------------------

export interface Objeto {
  id: string;
  /** O objeto da planta de onde este veio (o degrau aponta para a escada). */
  origem: string;
  andar: IdAndar;
  tipo: TipoObjeto;
  x0: number; y0: number; x1: number; y1: number;
  /** Base e topo, relativos ao piso do andar. */
  z0: number; z1: number;
  nome?: string;
  codigo?: string;
  zona?: CodigoZona | null;
  cor?: Cor;
  pontaVermelha?: boolean;
  marca?: MarcaDaCalota;
}

/**
 * Uma escada descrita UMA vez, na altura real (a partir da calçada), e
 * repartida entre os andares: cada degrau vai para o andar em que o topo dele
 * cai. É o que deixa a escada do sótão atravessar o 2º andar sem ninguém
 * precisar fazer a conta de onde ela corta.
 */
function degrausDaEscada(e: ObjetoDaPlanta): Objeto[] {
  const n = e.degraus ?? 12;
  const cotaDeSaida = ANDARES.find((a) => a.id === e.andar)!.cota + e.z;
  const espelho = e.alt / n;
  const aoLongoDeY = e.sobe === 'fundo' || e.sobe === 'frente' || e.sobe === undefined;
  const invertido = e.sobe === 'frente' || e.sobe === 'esquerda';
  const comprimento = aoLongoDeY ? e.fundo : e.larg;
  const passo = comprimento / n;
  const saida: Objeto[] = [];
  for (let i = 0; i < n; i += 1) {
    const topo = cotaDeSaida + espelho * (i + 1);
    // O último andar cuja cota não passa do topo do degrau (folga de 5 cm:
    // o degrau que chega rente ao piso de cima é o primeiro DELE).
    const andar = [...ANDARES].reverse().find((a) => a.cota <= topo - 0.05) ?? ANDARES[0]!;
    const a = invertido ? comprimento - passo * (i + 1) : passo * i;
    const b = a + passo;
    saida.push({
      id: `${e.id}-${i + 1}`,
      origem: e.id,
      andar: andar.id,
      tipo: 'degrau',
      x0: aoLongoDeY ? e.x : e.x + a,
      x1: aoLongoDeY ? e.x + e.larg : e.x + b,
      y0: aoLongoDeY ? e.y + a : e.y,
      y1: aoLongoDeY ? e.y + b : e.y + e.fundo,
      z0: 0,
      z1: Math.max(0.02, topo - andar.cota),
      nome: e.nome,
      zona: null,
    });
  }
  return saida;
}

/** A planta virada em caixas para o desenho. */
export function objetosDaPlanta(planta: PlantaDaLoja): Objeto[] {
  return planta.objetos.flatMap((o): Objeto[] => {
    if (o.tipo === 'escada') return degrausDaEscada(o);
    const saida: Objeto = {
      id: o.id, origem: o.id, andar: o.andar, tipo: o.tipo,
      x0: o.x, y0: o.y, x1: o.x + o.larg, y1: o.y + o.fundo,
      z0: o.z, z1: o.z + o.alt,
    };
    if (o.nome) saida.nome = o.nome;
    if (o.codigo) saida.codigo = o.codigo;
    if (o.zona !== undefined) saida.zona = o.zona;
    else if (o.tipo === 'piso' || o.tipo === 'parede') saida.zona = null;
    if (o.cor) saida.cor = o.cor;
    if (o.pontaVermelha) saida.pontaVermelha = true;
    if (o.marca) saida.marca = o.marca;
    return [saida];
  });
}

export const PLANTA_INICIAL: PlantaDaLoja = lerPlanta(plantaDoArquivo);
export const OBJETOS: readonly Objeto[] = objetosDaPlanta(PLANTA_INICIAL);

// --- Moldura: telhados e contorno dos andares ---------------------------------

/** Um pano de telhado: polígono livre, em coordenadas relativas ao andar. */
export interface Telhado {
  id: string;
  andar: IdAndar;
  vertices: ReadonlyArray<readonly [number, number, number]>;
}

const EIXO_CUMEEIRA = 4.0;   // x da cumeeira: o telhado cai para as duas laterais
const BEIRAL = 0.9;          // altura das paredes baixas das laterais do sótão
const CUMEEIRA = 2.9;
const FUNDO_SOTAO = 14.4;

export const TELHADOS: readonly Telhado[] = [
  {
    id: 'telhado-sotao-esq', andar: 3,
    vertices: [[-0.3, -0.3, BEIRAL - 0.1], [EIXO_CUMEEIRA, -0.3, CUMEEIRA], [EIXO_CUMEEIRA, FUNDO_SOTAO + 0.3, CUMEEIRA], [-0.3, FUNDO_SOTAO + 0.3, BEIRAL - 0.1]],
  },
  {
    id: 'telhado-sotao-dir', andar: 3,
    vertices: [[8.3, -0.3, BEIRAL - 0.1], [EIXO_CUMEEIRA, -0.3, CUMEEIRA], [EIXO_CUMEEIRA, FUNDO_SOTAO + 0.3, CUMEEIRA], [8.3, FUNDO_SOTAO + 0.3, BEIRAL - 0.1]],
  },
  {
    // O galpão dos fundos tem telhado próprio, de uma água, mais baixo que o prédio.
    id: 'telhado-fundos', andar: 1,
    vertices: [[-0.2, 17.5, 3.7], [10.1, 17.5, 3.7], [10.1, 26.8, 3.1], [-0.2, 26.8, 3.1]],
  },
];

/** Os cantos de cada andar — é por eles que passam as linhas tracejadas da vista explodida. */
export const CONTORNO_DO_ANDAR: Readonly<Record<IdAndar, ReadonlyArray<readonly [number, number]>>> = {
  1: [[0, 0], [8, 0], [8, 22], [9.9, 22], [9.9, 26.6], [0, 26.6]],
  2: [[0, 0], [8, 0], [8, 17.5], [0, 17.5]],
  3: [[0, 0], [8, 0], [8, FUNDO_SOTAO], [0, FUNDO_SOTAO]],
};

// --- Geometria de apoio -----------------------------------------------------

/** Ponto dentro de polígono (raio para a direita; borda conta como dentro só por acaso). */
export function dentro(x: number, y: number, poligono: ReadonlyArray<readonly [number, number]>): boolean {
  let dentroDele = false;
  for (let i = 0, j = poligono.length - 1; i < poligono.length; j = i, i += 1) {
    const [xi, yi] = poligono[i]!;
    const [xj, yj] = poligono[j]!;
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) dentroDele = !dentroDele;
  }
  return dentroDele;
}

/** A zona de um objeto: a forçada, ou a que contém o centro dele no mesmo andar. */
export function zonaDoObjeto(o: Pick<Objeto, 'zona' | 'andar' | 'x0' | 'x1' | 'y0' | 'y1'>): CodigoZona | null {
  if (o.zona !== undefined) return o.zona;
  const cx = (o.x0 + o.x1) / 2;
  const cy = (o.y0 + o.y1) / 2;
  return ZONAS.find((z) => z.andar === o.andar && dentro(cx, cy, z.contorno))?.codigo ?? null;
}

export const zonaPorCodigo = (codigo: CodigoZona): Zona => ZONAS.find((z) => z.codigo === codigo)!;
export const andarPorId = (id: IdAndar): Andar => ANDARES.find((a) => a.id === id)!;

/**
 * O tamanho da zona NO DESENHO: largura × fundo da caixa que a envolve, e a
 * área de verdade do contorno (a Z4 em L não conta o vão). É o que o dono
 * confere com a trena — e o número a corrigir aqui quando não bater.
 */
export function medidasDaZona(z: Zona): { largura: number; fundo: number; area: number } {
  const xs = z.contorno.map((p) => p[0]);
  const ys = z.contorno.map((p) => p[1]);
  let dobro = 0;
  for (let i = 0, j = z.contorno.length - 1; i < z.contorno.length; j = i, i += 1) {
    const [xi, yi] = z.contorno[i]!;
    const [xj, yj] = z.contorno[j]!;
    dobro += (xj + xi) * (yj - yi);
  }
  return {
    largura: Math.max(...xs) - Math.min(...xs),
    fundo: Math.max(...ys) - Math.min(...ys),
    area: Math.abs(dobro) / 2,
  };
}

/** Os nomes de lugar de uma zona, sem repetição, na ordem da planta. */
export function lugaresDaZona(codigo: CodigoZona, objetos: readonly Objeto[] = OBJETOS): string[] {
  const nomes = objetos
    .filter((o) => o.nome && zonaDoObjeto(o) === codigo)
    .map((o) => o.nome!);
  return [...new Set(nomes)];
}
